import { Box, Paper, Text, Stack, Textarea, Grid, Group, Select } from '@mantine/core';
import { DRAWER_PADDING } from 'utils/general';
import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';
import { PortalButton } from 'components/Button/PortalButton';
import { BsPlus } from 'react-icons/bs';
import { RadioContainer } from 'components/RadioContainer/RadioContainer';
import {
  MedicalEventBlankingPeriod,
  MedicalEventDataForm,
  MedicalEventTreatmentMethod,
  MedicalEventType,
} from 'models/patientMedicalEvent';
import { DateTimePicker } from '@mantine/dates';
import i18next from 'i18next';
import { EmptyParametersError } from 'services/errors/EmptyParametersError';
import { validateRequired } from 'utils/validation';
import { FormColumn } from 'components/Form/FormColumn';
import { useAddLog } from 'hooks/api/useLogs';
import { validateAddLogSubmission } from 'utils/logs';
import { useDrawerContext } from 'context/hooks';
import { PatientData } from 'models/user';
import { generalDateFormat, getTimeFormat } from 'utils/dates';
import { FormTreatmentSection } from './Form/FormTreatmentSection';
import { FormDiagnosticResultSection } from './Form/FormDiagnosticResultSection';

interface AddLogDrawerProps {
  patientData?: PatientData;
}
export const AddLogDrawer = ({ patientData }: AddLogDrawerProps) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const activeGroups = patientData?.visibleEnlistments || [];

  const form = useForm<MedicalEventDataForm>({
    initialValues: {
      blankingPeriodInWeeks: MedicalEventBlankingPeriod.EightWeeks.toString(),
    },
    validate: {
      type: (value) => validateRequired({ value, label: t('log_type') }),
      loggedTimestamp: (value) => validateRequired({ value, label: t('timestamp_label') }),
      groupId: (value) => validateRequired({ value, label: t('group_label') }),
      treatment: (value, values) =>
        values.type === MedicalEventType.Treatment && validateRequired({ value, label: t('add_log_name_label') }),
      procedure: (value, values) =>
        values.type === MedicalEventType.Treatment &&
        (values.treatment === MedicalEventTreatmentMethod.CatheterAblation ||
          values.treatment === MedicalEventTreatmentMethod.Cardioversion) &&
        validateRequired({ value, label: t('add_log_procedure_type') }),
      iteration: (value, values) =>
        values.type === MedicalEventType.Treatment &&
        values.treatment === MedicalEventTreatmentMethod.CatheterAblation &&
        validateRequired({ value, label: t('add_log_iteration') }),
      blankingPeriodInWeeks: (value, values) =>
        values.type === MedicalEventType.Treatment &&
        values.treatment === MedicalEventTreatmentMethod.CatheterAblation &&
        validateRequired({ value, label: t('date_label') }),
      diagnosticMethod: (value, values) =>
        values.type === MedicalEventType.DiagnosticResult && validateRequired({ value, label: t('add_log_method') }),
      diagnosticResult: (value, values) =>
        values.type === MedicalEventType.DiagnosticResult && validateRequired({ value, label: t('add_log_result') }),
    },
  });

  const onDrawerClose = () => {
    form.reset();
    closeDrawer();
  };

  const { mutate: addLogMutation, isPending: isPendingAddLog } = useAddLog({ callback: () => onDrawerClose() });

  const handleFormSubmit = async () => {
    if (!patientData) throw new EmptyParametersError();

    const submitData = validateAddLogSubmission({ form, patientData });

    addLogMutation(submitData);
  };

  const renderButtonGroup = () => {
    return (
      <Group mt={'xl'} justify={'flex-start'}>
        <PortalButton type={'submit'} loading={isPendingAddLog} label={t('add_log_label')} Icon={BsPlus} />
        <PortalButton variant={'outline'} onClick={() => closeDrawer()} label={t('cancel_label')} />
      </Group>
    );
  };

  const { type } = form.getValues();
  const { timeFormat, isTwelveHourFormat } = getTimeFormat();

  return (
    <Box px={DRAWER_PADDING}>
      <form onSubmit={form.onSubmit(handleFormSubmit)}>
        <Stack>
          <Paper radius={'md'} p={'md'} withBorder>
            <Stack>
              <Text>{`${t('log_type')}:`}</Text>
              <Grid>
                <Grid.Col span={6}>
                  <RadioContainer
                    key={MedicalEventType.Treatment}
                    radioId={MedicalEventType.Treatment}
                    leftText={t('add_log_log_type_treatment')}
                    setOption={() => form.setFieldValue('type', MedicalEventType.Treatment)}
                    selectedOption={type}
                    hasError={!!form.errors.type}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <RadioContainer
                    key={MedicalEventType.DiagnosticResult}
                    radioId={MedicalEventType.DiagnosticResult}
                    leftText={t('add_log_log_type_diagnostic-result')}
                    setOption={() => form.setFieldValue('type', MedicalEventType.DiagnosticResult)}
                    selectedOption={type}
                    hasError={!!form.errors.type}
                  />
                </Grid.Col>
              </Grid>
              <Grid align={'center'}>
                <FormColumn label={t('timestamp_label')}>
                  <DateTimePicker
                    required
                    withSeconds
                    locale={i18next.language}
                    value={new Date()}
                    valueFormat={`${generalDateFormat} ${timeFormat}`}
                    placeholder={t('pick_date_label')}
                    {...form.getInputProps('loggedTimestamp')}
                    timePickerProps={{
                      format: isTwelveHourFormat ? '12h' : '24h',
                    }}
                    clearable
                  />
                </FormColumn>
                <FormColumn label={t('group_label')}>
                  <Select
                    required
                    data={activeGroups.map((group) => ({
                      label: group.name,
                      value: group.groupId,
                    }))}
                    placeholder={t('group_placeholder')}
                    {...form.getInputProps('groupId')}
                  />
                </FormColumn>
              </Grid>
            </Stack>
          </Paper>
          {type === MedicalEventType.Treatment && <FormTreatmentSection form={form} />}
          {type === MedicalEventType.DiagnosticResult && <FormDiagnosticResultSection form={form} />}
          <Paper radius={'md'} p={'md'} withBorder>
            <Stack>
              <Text>{t('note_label')}</Text>
              <Textarea maxLength={500} autosize minRows={5} {...form.getInputProps('comment')} />
            </Stack>
          </Paper>
          {renderButtonGroup()}
        </Stack>
      </form>
    </Box>
  );
};
